// The "a trial registration really completed" signal for Google Tag Manager.
//
// Marketing's GTM container (loaded in `src/scripts`) has a trigger on the custom event
// `trial_signup_success` with `product: 'ai_recruiting'`, which fires the Google Ads
// conversion (and any GA4 event tag they attach). The event name and parameter are that
// trigger's contract — don't rename them here without changing the trigger.
//
// Every signup path redirects with `window.location.href` right after `POST /register`
// succeeds. Navigating immediately can cancel the conversion request GTM just started, so
// the redirect is handed in as `onDone` and runs from GTM's `eventCallback` (fired once its
// tags have finished), with a timeout so a blocked or slow GTM never strands the user.

declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[]
        /** Defined by gtm.js once the container has loaded. */
        google_tag_manager?: unknown
    }
}

export const TRIAL_SIGNUP_EVENT_TIMEOUT_MS = 2000

/** Push the trial-signup conversion, then call `onDone` (the post-signup redirect) exactly once. */
export function pushTrialSignupSuccess(onDone: () => void): void {
    let finished = false
    const finish = () => {
        if (finished) return
        finished = true
        onDone()
    }

    if (typeof window === 'undefined') return finish()

    try {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
            event: 'trial_signup_success',
            product: 'ai_recruiting',
            eventCallback: finish,
            eventTimeout: TRIAL_SIGNUP_EVENT_TIMEOUT_MS,
        })
    } catch {
        // Reporting must never block a registration that already succeeded.
        return finish()
    }

    // GTM only loads in production; elsewhere nothing will ever call `eventCallback`.
    if (!window.google_tag_manager) return finish()
    setTimeout(finish, TRIAL_SIGNUP_EVENT_TIMEOUT_MS)
}
